//clientInventory.tsx
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  SelectChangeEvent,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import bgImage from "../../asset/background.png";
import {
  fetchProductList,
  fetchStoreList,
  submitClientRequest,
} from "../../services/inventoryService";
import { RootState } from "../../store";
import ProductCard from "./ProductCard";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 400,
  // height: 500,
  // overflow: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ClientProducts: React.FC = () => {
  const [inventory, setInventory] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedProducts, setSelectedProducts] = useState<
    {
      product_id: string;
      name: string;
      qty: number;
      packed: number;
      saved: boolean;
      unit: string;
      msrp: number;
    }[]
  >([]);
  const [expandedCategories, setExpandedCategories] = useState<string>();
  const [selectedStore, setSelectedStore] = useState<any>();
  const [selectedReceivingStore, setSelectedReceivingStore] = useState<any>();
  const [stores, setStores] = useState<{ label: string; value: string }[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({});
  const [checkedSubs, setCheckedSubs] = useState<Record<string, boolean>>({});
  const [storeList, setStoreList] = useState<
    { label: string; value: string }[]
  >([]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const handleRowClick = (category: string) => {
    setExpandedCategories(category === expandedCategories ? "" : category);
  };
  const getSubState = (cat: any): "none" | "some" | "all" => {
    const subs = cat.subcategories.map((s: any) => s.name);
    const checked = subs.filter(
      (s: string) => checkedSubs[`${cat.category}|${s}`],
    );
    if (checked.length === 0) return "none";
    if (checked.length === subs.length) return "all";
    return "some";
  };

  const toggleCat = (catName: string) => {
    const cat = products.find((c: any) => c.category === catName);
    const newVal = getSubState(cat) !== "all";
    const updated = { ...checkedSubs };
    cat.subcategories.forEach((s: any) => {
      updated[`${catName}|${s.name}`] = newVal;
    });
    setCheckedSubs(updated);
    setExpandedCats((prev) => ({ ...prev, [catName]: true }));
  };

  const toggleSub = (catName: string, subName: string) => {
    const key = `${catName}|${subName}`;
    setCheckedSubs((prev) => ({ ...prev, [key]: !prev[key] }));
    setExpandedCats((prev) => ({ ...prev, [catName]: true }));
  };

  const toggleExpand = (catName: string) => {
    setExpandedCats((prev) => ({ ...prev, [catName]: !prev[catName] }));
  };
  // Fetch inventory data when the component mounts
  // const restructureData = (data: any) => {
  //   const result: any = [];

  //   data.forEach((item: any) => {
  //     const { category, sub_category, ...productDetails } = item;

  //     // Find or create category group
  //     let categoryGroup = result.find((c: any) => c.category === category);
  //     if (!categoryGroup) {
  //       categoryGroup = { category, products: [] };
  //       result.push(categoryGroup);
  //     }

  //     // Push product (ignore subcategory)
  //     categoryGroup.products.push(productDetails);
  //   });

  //   return result;
  // };
  const restructureData = (data: any) => {
    const result: any = [];

    data.forEach((item: any) => {
      const { category, sub_category, ...productDetails } = item;
      // check if not visible for client then contine to next wihtout adding
      if (productDetails.client_visible === false) {
        return;
      }
      // Find or create category group
      let categoryGroup = result.find((c: any) => c.category === category);
      if (!categoryGroup) {
        categoryGroup = { category, subcategories: [] };
        result.push(categoryGroup);
      }

      // Find or create subcategory group
      let subGroup = categoryGroup.subcategories.find(
        (s: any) => s.name === sub_category,
      );
      if (!subGroup) {
        subGroup = { name: sub_category, products: [] };
        categoryGroup.subcategories.push(subGroup);
      }

      // Push product into subcategory
      subGroup.products.push(productDetails);
    });

    return result;
  };

  const getWarehose = async () => {
    try {
      const data = await fetchStoreList("WAREHOUSE"); // Fetch the inventory data from an API
      setStores(
        data.map((item: any) => ({ label: item.name, value: item.store_id })),
      );
      const tempStore = data.find(
        (item: any) => item.store_id === "febbc26a-dff7-4220-91ad-319eaea4d511",
      );
      setSelectedStore({
        label: tempStore?.name as string,
        value: tempStore?.store_id as string,
      });
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStores = async () => {
    try {
      const data = await fetchStoreList("CLIENT"); // Fetch the inventory data from an API
      setStoreList(
        data.map((item: any) => ({ label: item.name, value: item.store_id })),
      );
      // console.log('Client list: ',data[0].name);
      setSelectedReceivingStore({
        label: data[0]?.name as string,
        value: data[0]?.store_id as string,
      });
      // setSelectedReceivingStore({
      //   label: tempStore?.name as string,
      //   value: tempStore?.store_id as string,
      // });
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoading(false);
    }
  };
  // useEffect(() => {
  //   const getInventoryData = async () => {
  //     try {
  //       const data = await fetchProductList(""); // Fetch the inventory data from an API
  //       if (data) {
  //         setProducts(
  //           restructureData(
  //             data.filter((item: any) => item.client_visible === true),
  //           ),
  //         );
  //       }
  //     } catch (error) {
  //       console.error("Error fetching inventory:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //   getWarehose();
  //   getStores();
  //   getInventoryData();
  // }, []);
  const getInventoryData = async () => {
    try {
      const data = await fetchProductList("");
      if (data) {
        const structured = restructureData(
          data.filter((item: any) => item.client_visible === true),
        );
        setProducts(structured);

        // Set checked/expanded right here — structured is guaranteed to be ready
        const { category } = location.state || {};
        const checked: Record<string, boolean> = {};
        const expanded: Record<string, boolean> = {};

        structured.forEach((cat: any) => {
          if (!category || cat.category === category) {
            expanded[cat.category] = true;
            cat.subcategories.forEach((sub: any) => {
              checked[`${cat.category}|${sub.name}`] = true;
            });
          }
        });

        setCheckedSubs(checked);
        setExpandedCats(expanded);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);

        await Promise.all([getWarehose(), getStores(), getInventoryData()]);
      } catch (error) {
        console.error("Failed to initialize client inventory", error);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleAddClick = (product: {
    product_id: string;
    name: string;
    packed: number;
    unit: string;
    msrp: number;
  }) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.product_id === product.product_id);
      if (exists) {
        return prev.map((p) =>
          p.product_id === product.product_id ? { ...p, saved: true } : p,
        );
      } else {
        // In case user clicks Add before entering qty
        return [
          ...prev,
          {
            product_id: product.product_id,
            name: product.name,
            qty: 0,
            packed: Number(product.packed),
            unit: product.unit,
            saved: true,
            msrp: product.msrp,
          },
        ];
      }
    });
  };

  const handleChangeStore = (event: SelectChangeEvent) => {
    // setSelectedStore(event.target.value as string);
    const tempStore = storeList.find((s) => s.value === event.target.value);
    setSelectedReceivingStore({
      label: tempStore?.label as string,
      value: tempStore?.value as string,
    });
    // setSelectedStore({
    //   label: tempStore?.label as string,
    //   value: tempStore?.value as string,
    // });
  };
  const location = useLocation();
  const { category } = location.state || {};

  // useEffect(() => {
  //   if (category) {
  //     setExpandedCategories(category);
  //   }
  // }, [category]);

  // Runs once when products finish loading

  // useEffect(() => {
  //   if (loading || products.length === 0) return;

  //   const { category } = location.state || {};

  //   if (category) {
  //     const checked: Record<string, boolean> = {};
  //     const expanded: Record<string, boolean> = {};

  //     products.forEach((cat: any) => {
  //       if (cat.category === category) {
  //         expanded[cat.category] = true;
  //         cat.subcategories.forEach((sub: any) => {
  //           checked[`${cat.category}|${sub.name}`] = true;
  //         });
  //       }
  //     });

  //     setCheckedSubs(checked);
  //     setExpandedCats(expanded);
  //   } else {
  //     const checked: Record<string, boolean> = {};
  //     const expanded: Record<string, boolean> = {};

  //     products.forEach((cat: any) => {
  //       expanded[cat.category] = true;
  //       cat.subcategories.forEach((sub: any) => {
  //         checked[`${cat.category}|${sub.name}`] = true;
  //       });
  //     });

  //     setCheckedSubs(checked);
  //     setExpandedCats(expanded);
  //   }
  // }, [products]); // 👈 depends on loading, not products

  const handleSubmit = async () => {
    try {
      if (!selectedReceivingStore?.value || !selectedStore?.value) {
        alert("Please select valid sending and receiving stores.");
        return;
      }

      const payload = selectedProducts
        .filter((product) => product.saved && product.qty > 0)
        .map((product) => ({
          ...product,
          notes,
        }));

      if (payload.length === 0) {
        alert(
          "Please add at least one product with a quantity greater than 0.",
        );
        return;
      }

      setLoadingState(true);

      const response = await submitClientRequest(
        payload,
        selectedStore.value,
        selectedReceivingStore.value,
      );

      if (response.status >= 200 && response.status < 300) {
        alert("Request submitted successfully");
        navigate("/orderHistory");
      }
    } catch (error) {
      console.error("Failed to submit client request:", error);
      alert("Something went wrong while submitting the request.");
    } finally {
      setLoadingState(false);
    }
  };

  const handleSummaryQtyChange = (
    e: ChangeEvent<HTMLInputElement>,
    productId: string,
  ) => {
    // change the sele ted product qty
    const value = Number(e.target.value);

    setSelectedProducts((prev) =>
      prev.map((product) =>
        product.product_id === productId ? { ...product, qty: value } : product,
      ),
    );
  };
  const handleQuantityChange = (
    value: number,
    product: {
      product_id: string;
      name: string;
      packed: number;
      unit: string;
      msrp: number;
    },
  ) => {
    // if incoming value is 0 then remove the product
    if (value === 0) {
      setSelectedProducts((prev) =>
        prev.filter((p) => p.product_id !== product.product_id),
      );
      return;
    }
    setProducts((prev) => {
      const existing = prev.find((p) => p.product_id === product.product_id);
      return prev.map((p) =>
        p.product_id === product.product_id ? { ...p, qty_packed: value } : p,
      );
    });
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.product_id === product.product_id);
      // console.log("Existing: ", existing, product.product_id, product.name);
      if (existing) {
        return prev.map((p) =>
          p.product_id === product.product_id ? { ...p, qty: value } : p,
        );
      } else {
        return [
          ...prev,
          {
            product_id: product.product_id,
            name: product.name,
            qty: value,
            packed: Number(product.packed),
            unit: product.unit,
            saved: false,
            msrp: product.msrp,
          },
        ];
      }
    });
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#f7f9f5",
          backgroundImage: `linear-gradient(rgba(247,249,245,0.92), rgba(247,249,245,0.92)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {loading ? (
          <CircularProgress
            sx={{ display: "block", margin: "40px auto", color: "#03360a" }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              height: "89vh",
              border: "1px solid #d6e4d0",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {/* ── Sidebar wrapper ── */}
            <Box
              sx={{
                display: "flex",
                flexShrink: 0,
                position: "relative",
                mt: 0.5,
              }}
            >
              {/* Sidebar */}
              <Box
                sx={{
                  width: sidebarOpen ? 210 : 0,
                  overflow: "hidden",
                  transition: "width 0.25s ease",
                  backgroundColor: "#fff",
                  borderRight: sidebarOpen ? "1px solid #d6e4d0" : "none",
                  overflowY: sidebarOpen ? "auto" : "hidden",
                  scrollbarWidth: "none",

                  pt: 2,
                }}
              >
                {products.map((cat: any) => {
                  const state = getSubState(cat);
                  const expanded = !!expandedCats[cat.category];
                  return (
                    <Box key={cat.category}>
                      {/* Category row */}
                      <Box
                        onClick={() => toggleExpand(cat.category)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 1,
                          mx: 0.75,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          transition: "background 0.15s",
                          "&:hover": { backgroundColor: "#f0f7ee" },
                        }}
                      >
                        {/* Category checkbox */}
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCat(cat.category);
                          }}
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            flexShrink: 0,
                            border:
                              state === "none"
                                ? "1.5px solid #d6e4d0"
                                : "1.5px solid #03360a",
                            backgroundColor:
                              state === "all" ? "#03360a" : "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.15s",
                          }}
                        >
                          {state === "all" && (
                            <svg width="9" height="9" viewBox="0 0 10 10">
                              <polyline
                                points="1.5,5 4,7.5 8.5,2.5"
                                fill="none"
                                stroke="#fff"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                          {state === "some" && (
                            <svg width="9" height="9" viewBox="0 0 10 10">
                              <line
                                x1="2"
                                y1="5"
                                x2="8"
                                y2="5"
                                stroke="#03360a"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                        </Box>

                        <Typography
                          fontSize={13}
                          fontWeight={600}
                          sx={{ flex: 1, color: "#03360a" }}
                        >
                          {cat.category}
                        </Typography>
                        <Typography
                          fontSize={11}
                          sx={{
                            color: "#4C763B",
                            lineHeight: 1,
                            transition: "transform 0.2s",
                            transform: expanded ? "rotate(180deg)" : "none",
                          }}
                        >
                          ▾
                        </Typography>
                      </Box>

                      {/* Subcategories */}
                      {expanded && (
                        <Box>
                          {cat.subcategories.map((sub: any) => {
                            const checked =
                              !!checkedSubs[`${cat.category}|${sub.name}`];
                            return (
                              <Box
                                key={sub.name}
                                onClick={() =>
                                  toggleSub(cat.category, sub.name)
                                }
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  pl: 4,
                                  pr: 1.5,
                                  py: 0.8,
                                  mx: 0.75,
                                  borderRadius: 1.5,
                                  cursor: "pointer",
                                  transition: "background 0.15s",
                                  "&:hover": { backgroundColor: "#f0f7ee" },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 15,
                                    height: 15,
                                    borderRadius: "3px",
                                    flexShrink: 0,
                                    border: checked
                                      ? "1.5px solid #03360a"
                                      : "1.5px solid #d6e4d0",
                                    backgroundColor: checked
                                      ? "#03360a"
                                      : "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.15s",
                                  }}
                                >
                                  {checked && (
                                    <svg
                                      width="8"
                                      height="8"
                                      viewBox="0 0 10 10"
                                    >
                                      <polyline
                                        points="1.5,5 4,7.5 8.5,2.5"
                                        fill="none"
                                        stroke="#fff"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </Box>
                                <Typography
                                  fontSize={12}
                                  sx={{
                                    color: checked ? "#03360a" : "#555",
                                    fontWeight: checked ? 500 : 400,
                                  }}
                                >
                                  {sub.name}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Toggle button */}
              <Box
                onClick={() => setSidebarOpen((prev) => !prev)}
                sx={{
                  position: "absolute",
                  right: -14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: 24,
                  height: 48,
                  backgroundColor: "#fff",
                  border: "1px solid #d6e4d0",
                  borderRadius: "0 8px 8px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background 0.15s",
                  "&:hover": { backgroundColor: "#eef4eb" },
                }}
              >
                <Typography
                  fontSize={11}
                  sx={{
                    color: "#4C763B",
                    transition: "transform 0.25s",
                    transform: sidebarOpen ? "rotate(0deg)" : "rotate(180deg)",
                    lineHeight: 1,
                  }}
                >
                  ◀
                </Typography>
              </Box>
            </Box>

            {/* ── Right panel ── */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  p: 2,
                }}
              >
                {(() => {
                  const visibleSubs: { catName: string; sub: any }[] = [];
                  products.forEach((cat: any) => {
                    cat.subcategories.forEach((sub: any) => {
                      if (checkedSubs[`${cat.category}|${sub.name}`]) {
                        visibleSubs.push({ catName: cat.category, sub });
                      }
                    });
                  });

                  if (visibleSubs.length === 0)
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#bbb",
                        }}
                      >
                        <Typography fontSize={13}>
                          Select a category or subcategory to browse products
                        </Typography>
                      </Box>
                    );

                  const totalProducts = visibleSubs.reduce(
                    (a, v) => a + v.sub.products.length,
                    0,
                  );

                  return (
                    <>
                      <Box
                        sx={{
                          mb: 2,
                          pb: 1,
                          borderBottom: "2px solid #e0ead9",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                        }}
                      >
                        <Typography
                          fontWeight={700}
                          fontSize={15}
                          sx={{ color: "#03360a" }}
                        >
                          {visibleSubs.length === 1
                            ? `${visibleSubs[0].catName} · ${visibleSubs[0].sub.name}`
                            : `${visibleSubs.length} subcategories selected`}
                        </Typography>
                        <Typography fontSize={12} color="#888">
                          {totalProducts} products
                        </Typography>
                      </Box>

                      {visibleSubs.map(({ catName, sub }) => (
                        <Box key={`${catName}|${sub.name}`} sx={{ mb: 3 }}>
                          {visibleSubs.length > 1 && (
                            <Typography
                              fontSize={11}
                              fontWeight={600}
                              sx={{
                                color: "#4C763B",
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                mb: 1,
                                pb: 0.5,
                                borderBottom: "1px solid #e8f0e4",
                              }}
                            >
                              {catName} · {sub.name}
                            </Typography>
                          )}
                          <Grid container spacing={1.5}>
                            {sub.products.map((product: any) => (
                              <Grid
                                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                                key={product.product_id}
                              >
                                <ProductCard
                                  product={product}
                                  discount_percent={
                                    user?.discount_percent as number
                                  }
                                  initialQty={
                                    selectedProducts.find(
                                      (p) =>
                                        p.product_id === product.product_id,
                                    )?.qty || 0
                                  }
                                  handleQuantityChange={handleQuantityChange}
                                  handleAddClick={handleAddClick}
                                  background={
                                    selectedProducts.find(
                                      (p) =>
                                        p.product_id === product.product_id,
                                    )?.saved
                                      ? "#03360a"
                                      : ""
                                  }
                                  btnType={
                                    selectedProducts.find(
                                      (p: any) =>
                                        p.product_id === product.product_id,
                                    )?.saved
                                      ? "contained"
                                      : "outlined"
                                  }
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ))}
                    </>
                  );
                })()}
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2.5,
                  py: 1,
                  backgroundColor: "#fff",
                  borderTop: "1px solid #d6e4d0",
                }}
              >
                <Typography fontSize={13} color="#239848" fontWeight={500}>
                  {selectedProducts.filter((p) => p.saved).length} item(s) in
                  order
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Add a note"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={{
                      width: 180,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&.Mui-focused fieldset": { borderColor: "#03360a" },
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#03360a" },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleOpen}
                    disabled={
                      selectedProducts.filter((p) => p.saved).length === 0
                    }
                    sx={{
                      backgroundColor: "#03360a",
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      "&:hover": { backgroundColor: "#11671c" },
                      "&:disabled": { backgroundColor: "#d0d0d0" },
                    }}
                  >
                    Review Order
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            // minWidth: '60vw',
            width: { md: "50vw", sm: "60vw", xs: "90vw" },
            bgcolor: "background.paper",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: 24,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Review before placing
              </Typography>
              <Typography variant="h6" fontWeight={500}>
                {loadingState === true ? "Processing ..." : "Order Summary"}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "success.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShoppingBagIcon fontSize="small" color="success" />
            </Box>
          </Box>
          {loadingState === false ? (
            <>
              {/* Items */}
              <Box sx={{ maxHeight: "220px", overflowY: "auto", px: 3, pb: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Product", "Unit", "Packge Price", "Qty", "Price"].map(
                        (h) => (
                          <TableCell
                            key={h}
                            align={
                              h === "Price"
                                ? "right"
                                : h === "Qty"
                                  ? "center"
                                  : "left"
                            }
                            sx={{
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: 1,
                              color: "text.disabled",
                              fontWeight: 600,
                              backgroundColor: "#e7e7e7",
                            }}
                          >
                            {h}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedProducts
                      .filter((p) => p.saved)
                      .map((item) => (
                        <TableRow key={item.product_id}>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={500}>
                              {item.name}
                            </Typography>
                            <Typography fontSize={11} color="text.disabled">
                              Pack of {item.packed} · {item.unit}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ color: "text.secondary", fontSize: 13 }}
                          >
                            $
                            {Number(
                              (item.msrp / item.packed) *
                                (1 - (user?.discount_percent || 0) / 100),
                            ).toFixed(2)}{" "}
                            / {item.unit}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ color: "text.secondary", fontSize: 13 }}
                          >
                            {Number(
                              item.msrp *
                                (1 - (user?.discount_percent || 0) / 100),
                            ).toFixed(2)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ color: "text.secondary", fontSize: 13 }}
                          >
                            <TextField
                              value={item.qty}
                              type="number"
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handleSummaryQtyChange(e, item.product_id)
                              }
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 500, fontSize: 13 }}
                          >
                            $
                            {Number(
                              item.msrp *
                                item.qty *
                                (1 - (user?.discount_percent || 0) / 100),
                            ).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Totals */}
              <Box
                sx={{
                  px: 3,
                  py: 1.5,
                  bgcolor: "grey.50",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {[
                  {
                    label: "Subtotal",
                    value: selectedProducts
                      .filter((p) => p.saved)
                      .reduce(
                        (t, i) =>
                          t +
                          Number(
                            i.msrp *
                              i.qty *
                              (1 - (user?.discount_percent || 0) / 100),
                          ),
                        0,
                      ),
                  },
                  // {
                  //   label: "Tax (13% HST)",
                  //   value:
                  //     selectedProducts
                  //       .filter((p) => p.saved)
                  //       .reduce(
                  //         (t, i) =>
                  //           t +
                  //           Number(
                  //             i.msrp *
                  //               i.qty *
                  //               (1 - (user?.discount_percent || 0) / 100),
                  //           ),
                  //         0,
                  //       ) * 0.13,
                  // },
                ].map(({ label, value }) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.5,
                    }}
                  >
                    <Typography fontSize={13} color="text.secondary">
                      {label}
                    </Typography>
                    <Typography fontSize={13} color="text.secondary">
                      ${value.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                    mt: 0.5,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography fontSize={15} fontWeight={500}>
                    Total
                  </Typography>
                  <Typography fontSize={17} fontWeight={500}>
                    $
                    {selectedProducts
                      .filter((p) => p.saved)
                      .reduce(
                        (t, i) =>
                          t +
                          Number(
                            i.msrp *
                              i.qty *
                              (1 - (user?.discount_percent || 0) / 100),
                          ),
                        0,
                      )
                      // * 1.13
                      .toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Place Order
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleClose}
                  sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <Paper
                elevation={4}
                sx={{
                  width: 420,
                  p: 5,
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <>
                  <CircularProgress color="success" size={55} />

                  <EmailRoundedIcon
                    sx={{
                      fontSize: 70,
                      color: "primary.main",
                      mt: 3,
                      mb: 2,
                    }}
                  />

                  <Typography variant="h5" fontWeight={600}>
                    Sending Confirmation Email
                  </Typography>

                  <Typography color="text.secondary" mt={1}>
                    Your transfer has been created successfully.
                    <br />
                    We're sending your invoice to your email.
                  </Typography>
                </>
              </Paper>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ClientProducts;
